import React, { useContext, FC, Suspense, useMemo, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { FormProvider } from 'react-hook-form';
import Blade from '../Blade';
import { Loader } from '../Loader';
import { Pagination } from '../Pagination';
import { PropertyCard } from './PropertyCard';
import { PropertySearch } from '../search/PropertySearch';
import { SearchFacets } from '../search/SearchFacets';
import { Section } from '../Section';
import { LISTINGS } from '../../const';
import { useSearch } from '../../hooks/useSearch';
import { useStandardFacets } from '../../hooks/useStandardFacets';
import { Kfh_AreaType, Kfh_PropertyTypeInterfaceEdge, MyKfhCreateSavedSearchInput } from '../../types/graphqlTypes';
import { SEO } from '../Seo';
import { SearchCategory, TypeOfSearch } from '../../types/enums';
import { SearchActions } from './SearchActions';
import { MapListingContextProvider, MapListingContext } from '../../context/MapListingContext';
import { StickyViewContainer } from '../StickyViewContainer';
import { UiStateContext } from '../../context/UiStateContext';
import { PropertyListingBanner } from './PropertyListingBanner';
import { SimplePageHeader } from '../pageHeader/SimplePageHeader';
import { PropertySearchCommercial } from '../search/PropertySearchCommercial';
import { SearchFacetsCommercial } from '../search/SearchFacetsComercial';
import { PropertySearchTypes, SearchCategories } from '../../types/properties';
import { useSeoData } from '../../hooks/useSeoData';
import { CreatePropertyAlertBlade } from '../my-kfh/CreatePropertyAlertBlade';
import { SavePropertyLoginOrRegisterBlade } from '../my-kfh/SavePropertyLoginOrRegisterBlade';
import { RegisterBlade } from '../my-kfh/RegisterBlade';
import { CreatePropertyAlertLoginOrRegisterBlade } from '../my-kfh/CreatePropertyAlertLoginOrRegisterBlade';
import { PropertiesNoResults } from './PropertiesNoResults';
import { CTACard } from '../CtaCard';
import { RequestAValuationBlade } from './RequestAValuationBlade';
import { isBrowser } from '../../utils/isBrowser';
import { ExperimentsContext } from '../../context/ExperimentsContext';
import { RecentlyViewedProperties } from './RecentlyViewedProperties';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { Notice } from '../Notice';

const getOverrideValues = ({ location, searchType, category }) => {
  const overrideValues: Record<string, any> = location?.artirixMultiSearch
    ? { multiSearch: location.artirixMultiSearch, category }
    : { category };

  if (searchType === 'commercialProperties') {
    overrideValues.type = TypeOfSearch.Commercial;
  } else if (searchType === 'developmentResaleProperties') {
    overrideValues.type = TypeOfSearch.Development;
  } else {
    overrideValues.type = TypeOfSearch.Residential;
  }
  if (searchType === 'newHomeProperties') {
    overrideValues.onlynewhomes = true;
    overrideValues.type = TypeOfSearch.Residential;
  }

  if (searchType.includes('Let') || searchType.includes('Sold')) {
    overrideValues.unavailable = true;
    overrideValues.sort = 'DATE';
  }

  return overrideValues;
};

const getCategory = ({ searchCategory, searchType }) => {
  let category = SearchCategory.Resale;
  let saveSearchCategory;

  if (searchCategory === 'resale') {
    category = SearchCategory.Resale;
    saveSearchCategory = 'resale';
  } else if (searchCategory === 'rental') {
    category = SearchCategory.Rental;
    saveSearchCategory = 'rental';
  } else if (searchType?.includes('ForSale')) {
    category = SearchCategory.Resale;
    saveSearchCategory = 'resale';
  } else if (searchType?.includes('ForRent')) {
    category = SearchCategory.Rental;
    saveSearchCategory = 'rental';
  } else if (searchType?.includes('Let')) {
    category = SearchCategory.Rental;
    saveSearchCategory = 'rental';
  } else if (searchType?.includes('Sold')) {
    category = SearchCategory.Resale;
    saveSearchCategory = 'resale';
  }
  return { category, saveSearchCategory };
};

const PropertyMap = React.lazy(() => import('./PropertyMap'));
// Interfaces

interface PropertyListingProps {
  properties: Kfh_PropertyTypeInterfaceEdge[];
  location?: Kfh_AreaType;
  pageContext: PropertyListingContentContext;
  unavailable?: boolean;
  disableAlerts?: boolean;
}

interface PropertyListingContentContext {
  ids: number[];
  pageNumber: number;
  totalPages: number;
  locationId?: number;
  pathName: string;
  totalCount: number;
  searchType?: PropertySearchTypes;
  searchCategory?: SearchCategories;
}

// Styles

export const StyledPropertyGrid = styled.ul`
  ${({ theme: { media, maxMedia, space } }) => css`
    display: grid;
    gap: ${space.md};

    // Table > Desktop styles

    @media (min-width: ${media.lg}) {
      gap: ${space.lg};
    }

    // Table > < Desktop styles

    @media (min-width: ${media.sm}) and (max-width: ${maxMedia.lg}) {
      grid-template-columns: 1fr 1fr;
    }
  `};
`;

export const StyledValuationBanner = styled(CTACard)`
  ${({ theme: { media, maxMedia, space } }) => css`
    // Table > < Desktop styles

    @media (min-width: ${media.sm}) and (max-width: ${maxMedia.lg}) {
      grid-column: 1 / 3;
    }
  `};
`;

// JSX

const PropertyListingContent: FC<PropertyListingProps> = ({
  properties,
  location,
  pageContext,
  unavailable = false,
  disableAlerts = false,
}) => {
  // Page context
  const { pageNumber, pathName, totalCount, searchType, searchCategory, locationId } = pageContext;
  const { activeView } = useContext(MapListingContext);
  const { activeBlade, setActiveBlade } = useContext(UiStateContext);
  const { experiments } = useContext(ExperimentsContext);

  // Set the search category for filters
  const { category, saveSearchCategory } = getCategory({ searchCategory, searchType });

  // Hook to setup a search results page
  const overrideValues: Record<string, any> = getOverrideValues({ location, searchType, category });

  const {
    loading,
    searchMeta,
    formMethods,
    handleOnSubmit,
    activeProperty,
    setActiveProperty,
    handleReset,
    searchValues,
    activeFiltersCount,
    activeFilters,
  } = useSearch({
    overrideValues,
    multiSearchName: location?.artirixDisplayName || location?.name,
    searchFirstRunDefault: true,
  });

  // Search facets
  // We will wither get facets from areas or from the standard list
  // TODO check this data
  const standardFacets = useStandardFacets();
  const standardSeo = useSeoData();

  const searchFacets = useMemo(() => {
    if (locationId) {
      return location?.listingMeta.searchFacets;
    }

    if (searchType && standardFacets && standardFacets[searchType]) {
      return standardFacets[searchType]?.searchFacets;
    }

    return undefined;
  }, [locationId, searchType, standardFacets, location?.listingMeta.searchFacets]);

  // SEO
  const seo = locationId
    ? location?.listingMeta.seo
    : standardSeo.find(
        (s) =>
          s.searchType === searchType &&
          s.type === overrideValues.type &&
          s.category === category &&
          s.unavailable === unavailable
      );

  // Page URl

  const generatePageUrl = (pageNum: number): string => `${pathName}${pageNum > 1 ? `/${pageNum}` : ''}`;

  const multiSearch = location?.artirixMultiSearch
    ? { multiSearch: location?.artirixMultiSearch, name: location.artirixDisplayName || location.name }
    : undefined;

  // Save search data

  // API Path
  const saveSearchApiPath = locationId ? location?.listingMeta?.propertyEndpointApi : seo?.propertyEndpointApi;
  const saveSearchName = seo?.h1;
  const saveSearchLocations = locationId && location ? location.artirixDisplayName || location.name : '';
  const savedSearchDefault: MyKfhCreateSavedSearchInput = {
    alertSettings: [
      {
        deliveryFrequency: 'Weekly',
        deliveryDay: 'Monday',
        deliveryTime: '09:00',
      },
    ],
    apiPath: saveSearchApiPath,
    name: saveSearchName,
    url: isBrowser ? `${window.location.pathname}${window.location.search}` : '',
    payload: {
      locations: saveSearchLocations,
      category: saveSearchCategory,
      instantAlert: true,
      summaryAlert: false,
    },
  };

  const searchUnavailableType = () => {
    if (category === 'RESALE' && searchType === 'propertiesSold' && unavailable) {
      return 'SOLD';
    }

    if (category === 'RENTAL' && searchType === 'propertiesLet' && unavailable) {
      return 'LET';
    }
    return 'DEFAULT';
  };

  return (
    <FormProvider {...formMethods}>
      <>
        {pageContext?.searchType === 'commercialProperties' ? (
          <PropertySearchCommercial parameters={searchMeta} category={category} facets={searchFacets} />
        ) : (
          <PropertySearch
            parameters={searchMeta}
            facets={searchFacets}
            category={category}
            multiSearch={multiSearch}
            searchType={pageContext?.searchType}
            activeFiltersCount={activeFiltersCount}
          />
        )}
        <Blade
          id="propertyFilters"
          heading="Filters"
          primaryButton={{
            label: 'Apply filters',
            onClick: (e) => {
              handleOnSubmit(e);
              setActiveBlade(null);
            },
          }}
          secondaryButton={{
            label: 'Clear filters',
            onClick: () => {
              handleReset();
              setActiveBlade(null);
            },
          }}
        >
          {searchFacets && pageContext?.searchType !== 'commercialProperties' && (
            <SearchFacets
              facets={searchFacets}
              category={category}
              parameters={searchMeta}
              loading={loading}
              multiSearch={multiSearch}
              searchType={overrideValues.type as TypeOfSearch}
              activeFilters={activeFilters}
            />
          )}
          {searchFacets && pageContext?.searchType === 'commercialProperties' && (
            <SearchFacetsCommercial facets={searchFacets} parameters={searchMeta} loading={loading} />
          )}
        </Blade>

        <SimplePageHeader
          title={seo?.h1}
          meta={
            location?.listingMeta?.propertyIds?.fallbackSearch?.fallbackSearch
              ? `0 properties found`
              : `${totalCount} properties found`
          }
          tight
        />

        <SearchActions
          searchOrderType={searchUnavailableType()}
          disable={totalCount === 0}
          savedSearch={savedSearchDefault}
          disableAlerts={disableAlerts}
        />
        <StickyViewContainer>
          {activeView === 'list' && (
            <>
              <Section contained background="grey">
                {loading && <Loader />}

                <StyledPropertyGrid>
                  {location?.listingMeta?.propertyIds?.fallbackSearch?.fallbackSearch && (
                    <Notice
                      heading="Sorry, we couldn't find any properties in this area."
                      text={`${location?.listingMeta?.propertyIds?.fallbackSearch?.fallbackSearchReason}.`}
                    />
                  )}

                  {properties?.length >= 1 &&
                    properties.map((property, index) => {
                      // Check for a property banner

                      const propertyBanners = location?.listingMeta?.banners;
                      const currentPosition = index + 1;

                      // Experiment - update magazine banner position if in KFH22003 experiment variation
                      let banners = propertyBanners;

                      if (experiments?.KFH22003 === 'variation') {
                        banners = propertyBanners.map((banner) => {
                          if (banner.id === '3') {
                            return { ...banner, position: 9 };
                          }

                          return banner;
                        });
                      }

                      const currentBanner = banners?.filter(
                        (propertyBanner) => propertyBanner.position === currentPosition
                      )[0];

                      return (
                        <React.Fragment key={`property-listing-item-${index}`}>
                          {currentBanner && pageNumber === 1 ? (
                            <PropertyListingBanner banner={currentBanner.banner} key={`banner-${index}`} />
                          ) : (
                            ''
                          )}

                          <PropertyCard key={property.id} property={property} as="li" searchCategory={searchCategory} />

                          {experiments?.KFH22003 === 'variation' &&
                            pageContext.searchType.includes('Sale') &&
                            (currentPosition === 4 ||
                              (totalCount === 3 && currentPosition === 3) ||
                              (totalCount === 2 && currentPosition === 2) ||
                              (totalCount === 1 && currentPosition === 1)) &&
                            pageNumber === 1 && (
                              <StyledValuationBanner
                                key={`valuation-cta-${index}`}
                                title="Need a valuation?"
                                lead="Our local agents can provide an accurate valuation for your property."
                                backgroundColour="brand"
                                tightSpacing
                                inlineButton
                                customButton={{
                                  label: 'Request a valuation',
                                  onClick: () => setActiveBlade('propertyRequestAValuation'),
                                  eventClickBladeId: 'propertyRequestAValuation',
                                }}
                              />
                            )}
                        </React.Fragment>
                      );
                    })}
                </StyledPropertyGrid>
                {!loading && totalCount === 0 && (
                  <PropertiesNoResults handleSearchAgain={handleOnSubmit} locationName={location?.name} />
                )}
              </Section>
              {totalCount > LISTINGS.perPage && (
                <Section contained>
                  <Pagination
                    linkGenerator={generatePageUrl}
                    totalCount={totalCount}
                    currentPage={pageNumber}
                    pageSize={LISTINGS.perPage}
                  />
                </Section>
              )}
              {!pageContext.searchType.includes('Sale') ? (
                <RecentlyViewedProperties
                  divider={totalCount > LISTINGS.perPage}
                  usedIn="recently viewed carousel property listing"
                />
              ) : (
                ''
              )}
            </>
          )}
          {activeView === 'map' && (
            <Section contained background="white">
              <Suspense fallback={<Loader />}>
                <PropertyMap search={searchValues} setActiveProperty={setActiveProperty} />
              </Suspense>
            </Section>
          )}

          {/* Listing blades */}

          {/* Create a property alert */}

          {activeBlade === 'createPropertyAlertLoginOrRegister' && <CreatePropertyAlertLoginOrRegisterBlade />}
          {activeBlade === 'createPropertyAlert' && (
            <CreatePropertyAlertBlade
              apiPath={saveSearchApiPath}
              defaultName={saveSearchName}
              locations={saveSearchLocations}
              category={saveSearchCategory}
            />
          )}

          {/* Register */}
          <RegisterBlade property={activeProperty} />

          {/* Valuation */}
          <RequestAValuationBlade />
        </StickyViewContainer>
      </>
    </FormProvider>
  );
};

export const PropertyHead = ({ location, pageContext, unavailable = false }) => {
  const { searchType, searchCategory, locationId } = pageContext;

  // Set the search category for filters
  const { category } = getCategory({ searchCategory, searchType });

  // Hook to setup a search results page
  const overrideValues: Record<string, any> = getOverrideValues({ location, searchType, category });

  const standardSeo = useSeoData();
  // SEO
  const seo = locationId
    ? location?.listingMeta.seo
    : standardSeo.find(
        (s) =>
          s.searchType === searchType &&
          s.type === overrideValues.type &&
          s.category === category &&
          s.unavailable === unavailable
      );

  return <SEO data={seo} />;
};

export const PropertyListing: FC<PropertyListingProps> = (props) => (
  <MapListingContextProvider>
    <PropertyListingContent {...props} />
  </MapListingContextProvider>
);
export default PropertyListing;
